import { motion } from "framer-motion";
import { Instagram, Youtube } from "lucide-react";
import { INSTAGRAM_URL, YOUTUBE_URL } from "../constants/site";
import { ChapterHeader } from "./ChapterHeader";

const PHOTOS = [
  { src: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=85&w=1200&auto=format&fit=crop", caption: "Dubai · Rohit & Priya", tall: true },
  { src: "https://images.unsplash.com/photo-1717148665537-50fce0476b7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxiYWxpJTIwdGVtcGxlJTIwanVuZ2xlfGVufDB8fHx8MTc4ODE2NzY4N3ww&ixlib=rb-4.1.0&q=85", caption: "Bali · Anjali's honeymoon" },
  { src: "https://images.unsplash.com/photo-1715457573748-8e8a70b2c1be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxrYXNobWlyJTIwZGFsJTIwbGFrZSUyMHNoaWthcmF8ZW58MHx8fHwxNzg4MTY3Njk4fDA&ixlib=rb-4.1.0&q=85", caption: "Kashmir · Kulkarni family", tall: true },
  { src: "https://images.unsplash.com/photo-1745837112372-42e4e5b62e80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHx0aGFpbGFuZCUyMGJlYWNoJTIwc3Vuc2V0fGVufDB8fHx8MTc4ODE2NzY4N3ww&ixlib=rb-4.1.0&q=85", caption: "Phuket · Farah's first trip" },
  { src: "https://images.unsplash.com/photo-1496939376851-89342e90adcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxzaW5nYXBvcmUlMjBtYXJpbmElMjBiYXklMjBzYW5kcyUyMG5pZ2h0fGVufDB8fHx8MTc4ODE2NjkzfDA&ixlib=rb-4.1.0&q=85", caption: "Singapore · Bansal twins", tall: true },
  { src: "https://images.pexels.com/photos/34489429/pexels-photo-34489429.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", caption: "Kerala · Neha & Arjun" },
  { src: "https://images.unsplash.com/photo-1695395550316-8995ae9d35ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxyYWphc3RoYW4lMjBwYWxhY2UlMjBpbmRpYXxlbnwwfHx8fDE3ODgxNjc2ODd8MA&ixlib=rb-4.1.0&q=85", caption: "Udaipur · Anniversary trip" },
  { src: "https://images.unsplash.com/photo-1594342436424-dda50df715d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHx0aGFpbGFuZCUyMGJlYWNoJTIwc3Vuc2V0fGVufDB8fHx8MTc4ODE2NzY4N3ww&ixlib=rb-4.1.0&q=85", caption: "Goa · Office gang getaway", tall: true },
];

export const GallerySection = () => (
  <section id="gallery" data-testid="gallery-section" className="relative bg-obsidian px-6 py-28 md:px-10 md:py-36">
    <div className="mx-auto max-w-7xl">
      <ChapterHeader
        index="05"
        title="Travel Wall"
        testid="gallery-header"
        heading={
          <>
            Moments our travellers <span className="italic text-coral">brought home.</span>
          </>
        }
        blurb="A living wall of trips we've planned. Follow us for daily departures, visa wins and travel inspiration."
      />

      <div className="columns-2 gap-4 md:columns-3 lg:columns-4 [column-fill:_balance]">
        {PHOTOS.map((p, i) => (
          <motion.figure
            key={p.caption}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: (i % 4) * 0.08 }}
            data-testid={`gallery-photo-${i + 1}`}
            className="group relative mb-4 break-inside-avoid overflow-hidden rounded-2xl border border-white/10"
          >
            <img
              src={p.src}
              alt={p.caption}
              loading="lazy"
              className={`w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${p.tall ? "aspect-[3/4]" : "aspect-square"}`}
            />
            <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-4 pb-3 pt-10 text-xs tracking-wide text-white/90 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              {p.caption}
            </figcaption>
          </motion.figure>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row"
      >
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="gallery-instagram-btn"
          className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-[#f58529] via-[#dd2a7b] to-[#8134af] px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-transform duration-300 hover:scale-105"
        >
          <Instagram className="h-4 w-4" />
          Follow on Instagram
        </a>
        <a
          href={YOUTUBE_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="gallery-youtube-btn"
          className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/[0.04] px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white/85 transition-colors duration-300 hover:border-[#ff0000] hover:text-[#ff4444]"
        >
          <Youtube className="h-4 w-4 text-[#ff4444]" />
          Watch on YouTube
        </a>
      </motion.div>
    </div>
  </section>
);
